import { useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';

interface UseCloudflareCallOptions {
  onError?: (error: Error) => void;
  onTextReceived?: (text: string) => void;
  onTextDelta?: (delta: string) => void;
  onTextComplete?: () => void;
  scenarioId?: number;
  voice?: string;
  modelVersion?: string;
}

interface CloudflareCallsState {
  isInCall: boolean;
  sessionId: string | null;
  isTalking: boolean;
}

const DEBUG = true;

function log(...args: any[]) {
  if (DEBUG) {
    console.log('[CloudflareCalls]', ...args);
  }
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

export const useCloudflareCall = (options: UseCloudflareCallOptions = {}) => {
  const [state, setState] = useState<CloudflareCallsState>({
    isInCall: false,
    sessionId: null,
    isTalking: false
  });

  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const localAudioElement = useRef<HTMLAudioElement | null>(null);
  const remoteAudioElement = useRef<HTMLAudioElement | null>(null);
  const talkingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTalkingTimeout = () => {
    if (talkingTimeoutRef.current) {
      clearTimeout(talkingTimeoutRef.current);
      talkingTimeoutRef.current = null;
    }
  };

  const setTalkingState = (talking: boolean) => {
    clearTalkingTimeout();
    if (!talking) {
      talkingTimeoutRef.current = setTimeout(() => {
        setState(prev => ({ ...prev, isTalking: false }));
      }, 200);
    } else {
      setState(prev => ({ ...prev, isTalking: true }));
    }
  };

  const setupAudioElements = () => {
    // Create audio elements container
    const container = document.createElement('div');
    container.id = 'audioElements';
    container.style.marginBottom = '20px';

    // Set up local audio
    const localDiv = document.createElement('div');
    localAudioElement.current = document.createElement('audio');
    localAudioElement.current.id = 'localAudio';
    localAudioElement.current.autoplay = true;
    localAudioElement.current.muted = true;
    localAudioElement.current.controls = true;
    localDiv.appendChild(document.createTextNode('Local Audio: '));
    localDiv.appendChild(localAudioElement.current);
    container.appendChild(localDiv);

    // Set up remote audio
    const remoteDiv = document.createElement('div');
    remoteAudioElement.current = document.createElement('audio');
    remoteAudioElement.current.id = 'remoteAudio';
    remoteAudioElement.current.autoplay = true;
    remoteAudioElement.current.controls = true;
    remoteDiv.appendChild(document.createTextNode('Remote Audio: '));
    remoteDiv.appendChild(remoteAudioElement.current);
    container.appendChild(remoteDiv);

    document.body.appendChild(container);
  };

  const handleStartCall = useCallback(async () => {
    try {
      log('Starting call setup...');
      setupAudioElements();

      // 1. Create a new RTCPeerConnection
      const pc = new RTCPeerConnection({
        iceServers: [
          {
            urls: 'stun:stun.cloudflare.com:3478'
          }
        ]
      });
      peerConnection.current = pc;

      // Add connection state logging
      pc.oniceconnectionstatechange = () => {
        log('ICE Connection State:', pc.iceConnectionState);
      };
      pc.onconnectionstatechange = () => {
        log('Connection State:', pc.connectionState);
      };
      pc.onicegatheringstatechange = () => {
        log('ICE Gathering State:', pc.iceGatheringState);
      };
      pc.onicecandidate = (event) => {
        log('ICE Candidate:', event.candidate);
      };

      // 2. Set up local audio track
      log('Requesting media permissions...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      log('Media permissions granted');

      // Set local audio
      if (localAudioElement.current) {
        localAudioElement.current.srcObject = stream;
      }

      // 3. Add tracks to the peer connection
      stream.getTracks().forEach(track => {
        log('Adding track:', track.kind);
        pc.addTrack(track, stream);
      });

      // Set up remote audio handling
      pc.ontrack = (event) => {
        log('Received remote track:', event.track.kind);
        if (remoteAudioElement.current && event.streams[0]) {
          remoteAudioElement.current.srcObject = event.streams[0];
        }
      };

      // 4. Create offer
      log('Creating offer...');
      const offer = await pc.createOffer({
        offerToReceiveAudio: true
      });
      log('Offer created:', offer);
      await pc.setLocalDescription(offer);
      log('Local description set');

      // Wait for ICE gathering to complete
      if (pc.iceGatheringState !== 'complete') {
        log('Waiting for ICE gathering to complete...');
        await new Promise<void>((resolve) => {
          const checkState = () => {
            if (pc.iceGatheringState === 'complete') {
              resolve();
            } else {
              setTimeout(checkState, 100);
            }
          };
          checkState();
        });
      }
      log('ICE gathering completed');

      // 5. Send the offer to our API
      log('Sending offer to API...');
      const apiEndpoint = new URL(`${API_URL}/endpoint`);
      
      try {
        // Add voice and model parameters with safe defaults
        const safeVoice = options.voice || 'alloy';
        const safeModel = options.modelVersion || 'gpt-4o-realtime-preview-2024-12-17';
        
        // Safely encode parameters
        apiEndpoint.searchParams.set('voice', encodeURIComponent(safeVoice));
        apiEndpoint.searchParams.set('modelVersion', encodeURIComponent(safeModel));

        log('Using endpoint:', apiEndpoint.toString());
        
        const response = await fetch(apiEndpoint.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/sdp'
          },
          body: pc.localDescription?.sdp
        });

        if (!response.ok) {
          const errorText = await response.text();
          log('API request failed:', response.status, errorText);
          throw new Error(`API request failed: ${errorText}`);
        }

        // 6. Get the session details and SDP answer
        const answerSDP = await response.text();
        log('Received answer SDP');

        // 7. Set the remote description
        log('Setting remote description...');
        await pc.setRemoteDescription(new RTCSessionDescription({
          type: 'answer',
          sdp: answerSDP
        }));
        log('Remote description set');

        // 8. Handle audio events
        if (remoteAudioElement.current) {
          remoteAudioElement.current.onplaying = () => {
            log('Audio playback started');
            setTalkingState(true);
          };

          remoteAudioElement.current.onended = () => {
            log('Audio playback ended');
            setTalkingState(false);
          };

          remoteAudioElement.current.onerror = (e) => {
            log('Audio playback error:', e);
            setTalkingState(false);
          };
        }

        // 10. Update state
        setState(prev => ({
          ...prev,
          isInCall: true
        }));
        log('Call setup completed successfully');

      } catch (error) {
        log('API request error:', error);
        if (options.onError) {
          options.onError(error as Error);
        }
        toast.error('Failed to send offer to API');
      }

    } catch (error) {
      log('Start call error:', error);
      if (options.onError) {
        options.onError(error as Error);
      }
      toast.error('Failed to start call');
    }
  }, [options.onError, options.scenarioId, options.voice, options.modelVersion]);

  const handleEndCall = useCallback(async () => {
    try {
      // Close the peer connection
      if (peerConnection.current) {
        peerConnection.current.close();
        peerConnection.current = null;
      }

      // Stop audio playback
      if (remoteAudioElement.current) {
        remoteAudioElement.current.srcObject = null;
        remoteAudioElement.current.remove();  // Remove from DOM
        remoteAudioElement.current = null;
      }

      // Reset state
      setState({
        isInCall: false,
        sessionId: null,
        isTalking: false
      });

      // Clean up timeouts
      clearTalkingTimeout();

    } catch (error) {
      log('End call error:', error);
      if (options.onError) {
        options.onError(error as Error);
      }
    }
  }, []);

  return {
    ...state,
    startCall: handleStartCall,
    endCall: handleEndCall
  };
}; 